import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { noop } from "lodash";
import moment from "moment-timezone";
import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import Button from "../../../components/Button";
import Field from "../../../components/Field";
import CustomTimeInput from "../../../components/Inputs/CustomTimeInput";
import DateInput from "../../../components/Inputs/DateInput";
import DurationInput, { getDurationInputValue } from "../../../components/Inputs/DurationInput";
import MoneyInput from "../../../components/Inputs/MoneyInput";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import { applyTimezone, combineDateAndTime, renderDurationAsFormat } from "../../../utils/helpers/date";
import {
    renderOrgLevelResourceLabel,
    renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { getPrimaryActiveResource } from "../../../utils/helpers/user";
import { useCompanyTimeZone } from "../../../utils/hooks/company";
import { getUserName } from "../../Settings/ClockIn/Flyers/Form";
import { useCustomFields } from "../../Settings/CustomFields/context";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import LevelSelect from "../../TimeSheet/components/LevelSelect";
import { getCustomFieldInput } from "../../TimeSheet/components/TimeSheetTable/useColumns/useCustomFieldsColumns";

const AddPayCodeForm = ({
    mode,
    values,
    onFinish: _onFinish = noop,
    onClose = noop,
    loading,
    user,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { allocatedGroups } = useGroups();
    const { timesheetCustomFields } = useCustomFields();
    const companyTimezone = useCompanyTimeZone();

    const setUserDefaults = useCallback((user) => {
        if (!user) {
            return;
        }
        form.setFieldsValue({
            job: getPrimaryActiveResource(user?.jobs)?.id,
            location: getPrimaryActiveResource(user?.locations)?.id,
        })
    }, [form]);

    const getCustomFieldRules = useCallback((field) => {
        const values = [
            {
                required: field.isRequired,
                message: t("required-custom-field-type", { code: field.code }),
            },
        ];

        if (field.type === "email") {
            values.push({
                type: "email",
                message: t("invalid-email"),
            });
        }

        if (field.type === "number") {
            values.push({
                min: 0,
                message: t("custom-field-bigger-than-0", {
                    code: field.code,
                }),
            });
        }

        return values;
    }, [t]);

    const onFinish = useCallback((values) => {
        const user = values?.user;
        if (!user) {
            return;
        }
        const job = user.jobs?.find(({ id }) => id === values.job);
        const location = user.locations?.find(
            ({ id }) => id === values.location,
        );
        const data = {
            date: moment(values.date).format("YYYY-MM-DD"),
            user: user.id,
            location: location,
            job: job,
            payCode: values.payCode.id,
        };
        switch (values.payCode.hourType) {
            case "amount": {
                data.total = Number(values.total || 0);
                break;
            }
            case "unit": {
                data.duration = values.duration || 0;
                break;
            }
            default: {
                const timezone = location?.timezoneValue || companyTimezone;
                const startDateTime = combineDateAndTime(
                    moment(values.date),
                    moment(values.startDate)
                );
                data.startDate = applyTimezone(startDateTime, timezone, true)
                    .toISOString(true);
                const endDateTime = combineDateAndTime(
                    moment(values.date),
                    moment(values.endDate)
                );
                data.endDate = applyTimezone(endDateTime, timezone, true)
                    .toISOString(true);
                const isOvernight = moment
                    .parseZone(data.endDate)
                    .isSameOrBefore(moment.parseZone(data.startDate))
                if (isOvernight) {
                    data.endDate = moment
                        .parseZone(data.endDate)
                        .add(1, "day")
                        .toISOString(true);
                }
                break;
            }
        }
        data.customFields = values.customFields || null;
        for (let i = 1; i <= 4; i++) {
            const levelName = `level${i}`;
            if (values.user[levelName]) {
                data[levelName] = values.user[levelName].id || null;
            }
        }
        allocatedGroups?.forEach((group) => {
            const levelName = group.level.replace("_", "");
            if (values[levelName]) {
                data[levelName] = values[levelName].id || null;
            }
        });
        _onFinish(data);
    }, [_onFinish, companyTimezone, allocatedGroups]);

    useEffect(() => {
        if (!values?.data) {
            return;
        }
        const formValues = {
            date: values.data.date
                ? moment(values.data.date).toDate()
                : undefined,
            user: values.data.user,
            location: values.data.location?.id,
            job: values.data.job?.id,
            payCode: values.data.payCode,
            startDate: values.data.startDate
                ? moment.parseZone(values.data.startDate).toDate()
                : undefined,
            endDate: values.data.endDate
                ? moment.parseZone(values.data.endDate).toDate()
                : undefined,
            total: values.data.total,
            duration: values.data.duration,
            customFields: values.data.customFields || {},
        };

        allocatedGroups?.forEach((group) => {
            const levelName = group?.level?.replace("_", "");
            if (values[levelName]) {
                formValues[levelName] = values[levelName];
            }
        });

        form.setFieldsValue(formValues);
    }, [form, values, allocatedGroups]);

    useEffect(() => {
        if (!user) {
            return;
        }
        form.setFieldValue("user", user);
        setUserDefaults(user);
    }, [user, form, setUserDefaults]);

    return (
        <Form form={form} onFinish={onFinish} className="d-flex flex-column h-100">
            <div
                style={{ height: "calc(100% - 70px)" }}
                className="p-4 content overflow-auto"
            >
                <Row>
                    <Col md={6}>
                        <Field
                            className="w-100 d-flex flex-column"
                            name="date"
                            label={t("date")}
                            rules={[
                                {
                                    required: true,
                                    message: t("required-date"),
                                },
                            ]}
                        >
                            <DateInput
                                className="w-100"
                                disabled={mode === "view"}
                                dateFormat="YYYY-MM-DD"
                                placeholderText={t("date")}
                                onChange={() => {
                                    if (user) {
                                        return;
                                    }
                                    form.resetFields(["user", "location", "job"]);
                                }}
                            />
                        </Field>
                    </Col>

                    <Col md={6}>
                        <BaseField shouldUpdate>
                            {({ }, { }, { getFieldValue }) => {
                                let date = getFieldValue("date");
                                let resourcePath = axios.getUri({
                                    url: "/time-sheet/my-team",
                                });
                                if (date) {
                                    resourcePath = axios.getUri({
                                        url: "/time-sheet/my-team",
                                        params: {
                                            startDate: moment(date).format("YYYY-MM-DD"),
                                            endDate: moment(date).format("YYYY-MM-DD"),
                                        },
                                    });
                                }

                                return (
                                    <Field
                                        name="user"
                                        label={t("user")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-user"),
                                            },
                                        ]}
                                    >
                                        <ResourceSelect
                                            placeholder={t("user")}
                                            valuePropName="id"
                                            resourcePath={resourcePath}
                                            hasSearch
                                            renderLabel={getUserName}
                                            onChange={(user) => {
                                                setUserDefaults(user);
                                            }}
                                            disabled={mode === "view" || !!user || !date}
                                        />
                                    </Field>
                                );
                            }}
                        </BaseField>
                    </Col>
                </Row>

                <BaseField shouldUpdate>
                    {({ }, { }, { getFieldValue }) => {
                        const user = getFieldValue("user");
                        const locations = user?.locations || [];
                        const jobs = user?.jobs || [];

                        return (
                            <Row>
                                <Col md={6}>
                                    <Field
                                        name="location"
                                        label={t("location")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-location"),
                                            },
                                        ]}
                                    >
                                        <Select disabled={mode === "view" || !user}>
                                            {locations.map((location) => (
                                                <Select.Option key={location.id} value={location.id}>
                                                    {renderSettingsResourceLabel(location)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Field>
                                </Col>

                                <Col md={6}>
                                    <Field
                                        name="job"
                                        label={t("job")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-job"),
                                            },
                                        ]}
                                    >
                                        <Select disabled={mode === "view" || !user}>
                                            {jobs.map((job) => (
                                                <Select.Option key={job.id} value={job.id}>
                                                    {renderSettingsResourceLabel(job)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Field>
                                </Col>
                            </Row>
                        );
                    }}
                </BaseField>

                <BaseField>
                    {({ }, { }, { getFieldValue }) => {
                        const payCode = getFieldValue("payCode");

                        return (
                            <>
                                <Field
                                    name="payCode"
                                    label={t("pay-code")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("required-pay-code"),
                                        },
                                    ]}
                                >
                                    <ResourceSelect
                                        resourcePath="/pay-code?requiresApproval=true"
                                        renderLabel={renderSettingsResourceLabel}
                                        hasSearch
                                        placeholder={t("pay-code")}
                                        disabled={mode === "view"}
                                    />
                                </Field>

                                {payCode?.hourType === "amount" && (
                                    <Field
                                        className="w-100 d-flex flex-column"
                                        name="total"
                                        label={t("total")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-total"),
                                            },
                                        ]}
                                    >
                                        <MoneyInput
                                            className="w-100"
                                            placeholder={t("total")}
                                            disabled={mode === "view"}
                                        />
                                    </Field>
                                )}

                                {payCode?.hourType === "unit" && (
                                    <Field
                                        name="duration"
                                        label={t("duration")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-duration"),
                                            },
                                        ]}
                                    >
                                        {({ value, onChange }) => {
                                            return (
                                                <DurationInput
                                                    placeholder={t("duration")}
                                                    disabled={mode === "view"}
                                                    value={value ? renderDurationAsFormat(value, 'HH:mm') : undefined}
                                                    onChange={(value) => {
                                                        value = getDurationInputValue(value);
                                                        onChange(value);
                                                    }}
                                                />
                                            );
                                        }}
                                    </Field>
                                )}

                                {payCode?.hourType && payCode?.hourType !== "unit" && payCode?.hourType !== "amount" && (
                                    <div className="form-row">
                                        <div className="form-group col-md-6">
                                            <Field
                                                className="mb-0"
                                                name="startDate"
                                                label={t("start-time")}
                                                dependencies={["date"]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("required-start-date"),
                                                    },
                                                ]}
                                            >
                                                <CustomTimeInput
                                                    placeholder={t("start-time")}
                                                    disabled={mode === "view"}
                                                    className="w-100"
                                                />
                                            </Field>
                                        </div>

                                        <div className="form-group col-md-6">
                                            <Field
                                                className="mb-0"
                                                name="endDate"
                                                label={t("end-time")}
                                                dependencies={["startDate", "date"]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("required-end-date"),
                                                    },
                                                ]}
                                            >
                                                <CustomTimeInput
                                                    placeholder={t("end-time")}
                                                    disabled={mode === "view"}
                                                    className="w-100"
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    }}
                </BaseField>

                {allocatedGroups?.map((group) => (
                    <BaseField shouldUpdate key={group.id}>
                        {({ }, { }, { getFieldValue }) => {
                            const location = getFieldValue("location");
                            return (
                                <Field
                                    name={group?.level?.replace("_", "")}
                                    label={renderOrgLevelResourceLabel(group)}
                                    rules={[
                                        {
                                            required: group?.isRequired,
                                            message: t("required-group", {
                                                group: renderOrgLevelResourceLabel(group),
                                            }),
                                        },
                                    ]}
                                >
                                    <LevelSelect
                                        placeholder={renderOrgLevelResourceLabel(group)}
                                        group={group.id}
                                        location={location}
                                        allowClear={!group.isRequired}
                                        disabled={mode === "view"}
                                    />
                                </Field>
                            );
                        }}
                    </BaseField>
                ))}

                {timesheetCustomFields?.length > 0 && (
                    <div className="w-100 mt-4">
                        <hr />
                        <h5 className="mb-3 text-muted font-weight-400 text-uppercase">
                            {t("custom-fields")}
                        </h5>
                        <div
                            className="px-4"
                            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 30 }}
                        >
                            {timesheetCustomFields.map((field) => (
                                <Field
                                    key={field.id}
                                    label={field.description || field.code}
                                    name={["customFields", field.code]}
                                    rules={getCustomFieldRules(field)}
                                >
                                    {({ value, onChange }) => {
                                        return getCustomFieldInput({ config: field, value, onChange, disabled: mode === "view" });
                                    }}
                                </Field>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {mode !== "view" && (
                <div className="border-top py-3 text-center align-items-center justify-content-center">
                    <Button
                        className="btn-icon text-muted"
                        type="button"
                        color="white"
                        onClick={onClose}
                        loading={loading}
                    >
                        <span className="btn-inner--icon">
                            <FontAwesomeIcon icon={faCircleXmark} />
                        </span>
                        <span className="btn-inner--text">{t("cancel")}</span>
                    </Button>

                    <Button
                        className="btn-icon text-info"
                        type="submit"
                        color="white"
                        disabled={loading}
                    >
                        <span className="btn-inner--icon">
                            <FontAwesomeIcon icon={faCircleCheck} />
                        </span>

                        <span className="btn-inner--text">
                            {t("send-for-approval")}
                        </span>
                    </Button>
                </div>
            )}
        </Form>
    );
};

export default AddPayCodeForm;
